import { Button } from "@mui/material";
import { useCallback, useRef, useState } from "react";
import { useNotify, useRefresh, useTranslate } from "react-admin";

import {
  UnenrollDeviceDocument,
  UnenrollDeviceMutation,
  UnenrollDeviceMutationVariables,
} from "../../generated/graphql";
import { useErrorHandler } from "../../hooks/useErrorHandler";
import { client } from "../../lib/apolloClient";
import { CustomConfirm } from "../CustomConfirm";
import type { DeviceRecord } from "../DeviceShow";

type Props = {
  device: DeviceRecord;
};

export const UnenrollDeviceButton = ({ device }: Props) => {
  const translate = useTranslate();
  const notify = useNotify();
  const handleError = useErrorHandler();
  const refresh = useRefresh();
  const [isOpen, setIsOpen] = useState(false);
  const [isUnenrolling, setIsUnenrolling] = useState(false);
  const isSubmitting = useRef(false);

  const handleClose = useCallback(() => {
    if (isUnenrolling || isSubmitting.current) return;
    setIsOpen(false);
  }, [isUnenrolling]);

  const handleConfirm = useCallback(async () => {
    if (isUnenrolling || isSubmitting.current) return;

    isSubmitting.current = true;
    setIsUnenrolling(true);
    try {
      const result = await client.mutate<UnenrollDeviceMutation, UnenrollDeviceMutationVariables>({
        mutation: UnenrollDeviceDocument,
        variables: { id: device.id },
      });
      if (!result.data?.unenrollDevice) throw new Error("unenrollDevice returned no data");

      setIsOpen(false);
      notify("deviceEnrollment.unenroll.success", { type: "success" });
      refresh();
    } catch (error) {
      handleError(error, translate("deviceEnrollment.unenroll.error"), { operation: "unenrollDevice" });
    } finally {
      isSubmitting.current = false;
      setIsUnenrolling(false);
    }
  }, [device.id, handleError, isUnenrolling, notify, refresh, translate]);

  const normalizedStatus = device.status?.toLowerCase();
  if (device.unenrolledAt || (normalizedStatus !== "active" && normalizedStatus !== "inactive")) return null;

  const deviceName = device.name || device.deviceId || "—";
  const locationName = device.location?.name || "—";

  return (
    <>
      <Button
        color="secondary"
        onClick={() => setIsOpen(true)}
        disabled={isUnenrolling}
        data-testid="device-unenroll-button"
      >
        {translate("deviceEnrollment.unenroll.action")}
      </Button>
      <CustomConfirm
        isOpen={isOpen}
        loading={isUnenrolling}
        title={translate("deviceEnrollment.unenroll.title")}
        content={translate("deviceEnrollment.unenroll.content", { device: deviceName, location: locationName })}
        confirm={translate("deviceEnrollment.unenroll.action")}
        onConfirm={handleConfirm}
        onClose={handleClose}
        data-testid="device-unenroll-confirmation"
      />
    </>
  );
};
